import { buildSegment, carrierOf, doseQuantityOf, rechainTaper, segmentOn, timesPerInterval, todayIn } from '@/common/medicationSchedule';
import { hasMedicationReason } from '@/common/medicationPreferences';
import { presetItemErrors, presetStepToWindow } from '@/common/medicationPreset';
import { chainTaperSteps, classifyTaperSteps, emptyTaperSteps, isUnboundedTaperStep, taperStepDose, taperStepDuration, taperStepRate } from '@/common/medicationTaper';
import { blankWindow, enrolledSiteOf, intervalLabel, resizeTimes, usesDayOfWeek, windowFrequencyMismatch, windowPerInterval, windowSegmentInput } from '@/common/medicationWindow';
import {
    applyAdministrationInstructions,
    applyOrderDose,
    applyOrderPrn,
    applyOrderRoute,
    applyOrderTiming,
    composeSig,
    ensureCarrier,
    mergeCarrierTiming,
    rechainSegments,
} from '@/composables/useMedicationOrderEditor';
import { MedicationRequest } from '@/fhirworks';

/**
 * Applying a medication list preset to a patient: the review rows, how pass times fill them, what
 * blocks Save, and the MedicationRequests it writes.
 *
 * Every request is built with the add dialog's own order setters and the segment and taper dialogs'
 * own segment builders, so a medication added from a preset is the medication a clinician would
 * have entered by hand — editable, reconcilable and discontinuable the same way.
 */

// A read can hand back fhirworks models; the rows and the build work on plain JSON.
const plain = (value) => (value === undefined || value === null ? value : JSON.parse(JSON.stringify(value)));

const isScheduledWindow = (window) => (window?.type || 'scheduled') === 'scheduled';

const stepLabel = (index) => `Step ${index + 1}: `;

/**
 * One review row per preset item, in order.
 *
 * @param {object} preset - BN_MedicationPreset (model or JSON)
 * @param {{emarEnabled: boolean, accountWindow: number}} options
 * @returns {Array<object>}
 */
export const presetApplyRows = (preset, { emarEnabled, accountWindow }) =>
    (plain(preset?.item) || []).map((item, index) => {
        const incomplete = Object.keys(presetItemErrors(item)).length > 0;
        const type = item?.administrationType;
        const row = {
            key: item?.id ?? `item${index}`,
            item,
            type,
            checked: !incomplete,
            incomplete,
            doseValue: item?.dose?.value ?? null,
            patientInstruction: item?.patientInstruction || '',
            // Shown and required only when the account requires a reason; prefilled from the preset.
            reasonText: item?.reasonCode?.[0]?.text || '',
        };

        if (!emarEnabled || incomplete) return row;

        if (type === 'scheduled') {
            row.window = {
                ...blankWindow({
                    frequency: item.frequency,
                    period: item.period,
                    periodUnit: item.periodUnit || 'd',
                    times: [],
                    dayOfWeek: [],
                    administrationWindow: accountWindow,
                }),
                timesEdited: false,
            };
        }

        if (type === 'taper') {
            row.steps = (item.taperStep || []).map((step, stepIndex) => {
                const window = { ...presetStepToWindow(step, stepIndex, item), times: [], dayOfWeek: [], timesEdited: false };
                // Blank is the account's window at apply time (phase 1).
                if (isScheduledWindow(window) && !(Number(window.administrationWindow) > 0)) window.administrationWindow = accountWindow;
                if (step?.holdReason) window.holdReason = step.holdReason;
                return window;
            });
        }
        return row;
    });

/**
 * How many time slots the window's interval needs.
 *
 * @param {object} window
 * @returns {number}
 */
export const slotCount = (window) => timesPerInterval(window?.frequency, { periodUnit: window?.periodUnit, dayOfWeek: window?.dayOfWeek || [] });

const scheduledWindowsOf = (row) => [row?.window, ...(row?.steps || [])].filter((window) => window && isScheduledWindow(window));

/**
 * How many pass times the review step opens with: enough for the most frequent checked schedule.
 *
 * @param {Array<object>} rows
 * @returns {number} at least 1
 */
export const defaultPassTimeCount = (rows) =>
    (rows || [])
        .filter((row) => row.checked)
        .flatMap(scheduledWindowsOf)
        .reduce((most, window) => Math.max(most, slotCount(window)), 1);

/**
 * The window with its times taken from the pass times in order: an N-per-interval window takes the
 * first N, and a slot no pass time covers stays empty. Times typed on the row are kept, resized.
 *
 * @param {object} window
 * @param {Array<string|null>} passTimes
 * @returns {object}
 */
export const fillFromPassTimes = (window, passTimes) => {
    if (!window || !isScheduledWindow(window)) return window;
    if (window.timesEdited) return { ...window, times: resizeTimes(window, window.frequency) };
    const times = Array.from({ length: slotCount(window) }, (item, index) => passTimes?.[index] || null);
    return { ...window, times };
};

const stepDoseValue = (row, step, index) => (index === 0 ? row.doseValue : step.doseValue);

/**
 * Indexes of the taper steps that raise the daily dose — a titration, re-acknowledged on every
 * apply. Measured on each step's stated frequency, so acknowledgements don't flicker while times are
 * being filled.
 *
 * @param {object} row
 * @returns {number[]}
 */
export const ascendingSteps = (row) => {
    if (row?.type !== 'taper' || !row.steps?.length) return [];
    const entries = row.steps.map((step, index) => ({
        rate: isScheduledWindow(step) ? taperStepRate({ doseValue: stepDoseValue(row, step, index), perInterval: step.frequency, period: step.period, periodUnit: step.periodUnit }) : null,
        doseValue: Number(stepDoseValue(row, step, index)),
    }));
    return classifyTaperSteps(entries).ascending;
};

// What keeps a Scheduled window from becoming a schedule. None of it blocks Save: the row is added
// without a schedule instead (Jayte, 2026-10-22).
const windowGaps = (window) => {
    const daysMissing = usesDayOfWeek(window) && !(window.dayOfWeek || []).length;
    const slotEmpty = !(window.times || []).length || (window.times || []).some((time) => !time);
    return { daysMissing, slotEmpty, mismatch: !daysMissing && !slotEmpty && windowFrequencyMismatch(window) };
};

const windowReady = (window) => {
    if (!isScheduledWindow(window)) return true;
    const { daysMissing, slotEmpty, mismatch } = windowGaps(window);
    return !daysMissing && !slotEmpty && !mismatch;
};

const mismatchNote = (window, prefix = '') => {
    if (!isScheduledWindow(window) || !windowGaps(window).mismatch) return [];
    const per = intervalLabel(window).replace('every ', '');
    return [`${prefix}${window.frequency} per ${per} is ordered and the times give ${windowPerInterval(window)}. Match the times and days to the frequency.`];
};

/**
 * Whether the row will be added without the schedule or taper steps it was offered: a Scheduled
 * window, or any Scheduled taper step, missing a time or its days, or whose times don't give its
 * frequency. Such a row is added as its order alone, the way a medication added by hand with no
 * schedule is, and its times are never saved half-done.
 *
 * @param {object} row
 * @returns {boolean}
 */
export const addsUnscheduled = (row) => {
    if (row?.window) return !windowReady(row.window);
    if (row?.type === 'taper' && row.steps?.length) return !row.steps.every(windowReady);
    return false;
};

export const UNSCHEDULED_NOTE = 'Added without a schedule — set it from the medication.';
export const TAPER_ORDER_ONLY_NOTE = 'Added without its taper steps — open the medication and set the taper.';

/**
 * What the row will be saved without, said before Save. [] when it will be saved as shown, and
 * always [] for an unchecked row.
 *
 * @param {object} row
 * @returns {string[]}
 */
export const rowNotes = (row) => {
    if (!row?.checked || !addsUnscheduled(row)) return [];
    if (row.window) return [UNSCHEDULED_NOTE, ...mismatchNote(row.window)];
    return [TAPER_ORDER_ONLY_NOTE, ...row.steps.flatMap((step, index) => mismatchNote(step, stepLabel(index)))];
};

// The taper dialog's own empty-step rule, over the steps chained from the batch start.
const emptyStepIndexes = (row, start) => emptyTaperSteps(row.steps, chainTaperSteps(row.steps, start, { afterFinal: row.item?.afterFinal }));

const rowReasonText = (row) => String(row?.reasonText || '').trim();

// The row's text is the item's, so the build keeps the item's reason as it is, coding and all.
const keepsItemReason = (row) => rowReasonText(row) === String(row?.item?.reasonCode?.[0]?.text || '').trim();

// What the build writes, judged by the one reason rule: changed text, or the item's reason kept.
const rowHasReason = (row) => Boolean(rowReasonText(row)) || (keepsItemReason(row) && hasMedicationReason(row?.item?.reasonCode));

/**
 * What stops this row being saved. [] when it is ready, and always [] for an unchecked row. Missing
 * times and days never do: the row is added without a schedule (rowNotes says so).
 *
 * @param {object} row
 * @param {{start: string, acknowledged?: Set<number>, requireReason?: boolean}} options
 * @returns {string[]}
 */
export const rowProblems = (row, { start, acknowledged = new Set(), requireReason = false } = {}) => {
    if (!row?.checked) return [];
    const problems = [];
    if (!(Number(row.doseValue) > 0)) problems.push('Enter a dose');
    if (requireReason && !rowHasReason(row)) problems.push('Enter a reason for taking medication');

    // Only a taper whose steps will be written can run a step empty or raise the dose by itself.
    if (row.type === 'taper' && row.steps?.length && !addsUnscheduled(row)) {
        emptyStepIndexes(row, start).forEach((index) => problems.push(`${stepLabel(index)}No dose falls inside this step. Choose other days or a later start date.`));
        ascendingSteps(row)
            .filter((index) => !acknowledged.has(index))
            .forEach((index) => problems.push(`${stepLabel(index)}confirm the increase`));
    }
    return problems;
};

/**
 * Whether the batch has to name an administration site: only where sites are offered and none is the
 * patient's single enrollment, which would otherwise be the answer.
 *
 * @param {{emarEnabled: boolean, sites: Array}} options
 * @returns {boolean}
 */
export const siteRequired = ({ emarEnabled, sites }) => Boolean(emarEnabled) && (sites || []).length > 0 && !enrolledSiteOf(sites);

/**
 * What stops the batch being saved, whatever its rows say.
 *
 * @param {object} options
 * @returns {{start?: string, site?: string, none?: string, loading?: boolean}}
 */
export const batchProblems = ({ rows, start, today, emarEnabled, sites, siteId, sitesLoading }) => {
    const problems = {};
    if (!start) problems.start = 'Choose a start date';
    else if (today && start < today) problems.start = 'A schedule starts today or later.';
    if (siteRequired({ emarEnabled, sites }) && !siteId) problems.site = 'Choose an administration site';
    if (!(rows || []).some((row) => row.checked)) problems.none = 'Choose at least one medication';
    // The site rule is judged on the sites, so not before they have arrived.
    if (sitesLoading && emarEnabled) problems.loading = true;
    return problems;
};

const requesterFrom = (requester) => {
    const display = typeof requester?.display === 'string' ? requester.display.trim() : '';
    if (!display) return undefined;
    return requester.id ? { id: requester.id, resourceType: 'Practitioner', display } : { display };
};

/**
 * One MedicationRequest per checked, complete row, in row order — shaped exactly as one entered by
 * hand through the add dialog, the segment dialog and the taper dialog. A row addsUnscheduled marks
 * is written as its order alone, as one added by hand with no schedule or taper set.
 *
 * @param {object} options
 * @returns {Array<MedicationRequest>}
 */
export const buildPresetMedicationRequests = ({ preset, rows, start, requester, siteId, patientId, practitioner, emarEnabled, timeZone, now }) => {
    const nowIso = now instanceof Date ? now.toISOString() : now || new Date().toISOString();
    const addedBy = practitioner?.id ? { id: practitioner.id, resourceType: practitioner.resourceType || 'Practitioner' } : undefined;
    const site = siteId ? { id: siteId, resourceType: 'Location' } : undefined;
    const prescriber = requesterFrom(requester);

    return (rows || [])
        .filter((row) => row.checked && !row.incomplete)
        .map((row) => {
            const item = row.item || {};
            const type = item.administrationType;

            const med = new MedicationRequest({
                subject: { id: patientId, resourceType: 'Patient' },
                status: 'active',
                administrationType: type,
                medication: { CodeableConcept: plain(item.medication) },
                intent: 'proposal',
                effective: { start },
                dateAsserted: nowIso,
                reported: { reference: { id: patientId, resourceType: 'Patient' } },
                includeInTreatmentPlan: item.includeInTreatmentPlan === true,
            });

            // The row's text when it was changed; otherwise the preset's reason as it is, so a coded one survives.
            if (!keepsItemReason(row)) {
                const reasonText = rowReasonText(row);
                if (reasonText) med.reasonCode = [{ text: reasonText }];
            } else {
                const reasons = (item.reasonCode || []).filter((reason) => hasMedicationReason([reason]));
                if (reasons.length) med.reasonCode = plain(reasons);
            }
            if (prescriber) med.requester = { ...prescriber };
            if (practitioner?.id) med.updatedBy = { id: practitioner.id, resourceType: practitioner.resourceType };
            med.addedFromPreset = { id: preset?.id, resourceType: 'BN_MedicationPreset', display: preset?.name };

            applyOrderDose(med, { value: row.doseValue, unit: item.dose?.unit });
            applyOrderRoute(med, item.route?.text);
            if (type === 'scheduled' || type === 'taper') applyOrderTiming(med, { frequency: item.frequency, period: item.period, periodUnit: item.periodUnit });
            if (type === 'prn') applyOrderPrn(med, { maxPerDay: item.maxPerDay, minimumIntervalValue: item.minimumInterval?.value, minimumIntervalUnit: item.minimumInterval?.code });
            applyAdministrationInstructions(med, row.patientInstruction);

            // Only the carrier exists so far, so a schedule is appended to it.
            const carriers = () => [...(med.dosageInstruction || [])];

            // A row missing its times or days is added unscheduled, as a medication entered by hand
            // with no schedule is; its times are dropped, never saved half-done.
            if (type === 'scheduled' && emarEnabled && row.window && !addsUnscheduled(row)) {
                // BnMedicationSegmentDialog.save, then onSegmentSaved.
                const segment = buildSegment({ medication: med, sequence: 1, ...windowSegmentInput(row.window), start, addedBy, addedOn: nowIso, administeringLocation: site });
                med.dosageInstruction = [...carriers(), segment];
                rechainSegments(med);
            }

            if (type === 'prn') {
                // saveMedication's PRN segment, built whatever the eMAR flag says, as the add dialog does.
                const carrier = carrierOf(med);
                const segment = buildSegment({ medication: med, type: 'prn', start, maxPerDay: carrier.maxPerDay, minimumInterval: carrier.minimumInterval, addedBy, addedOn: nowIso });
                med.dosageInstruction = [...carriers(), segment];
                rechainSegments(med);
                if (emarEnabled && site) med.administeringLocation = { ...site };
            }

            if (type === 'taper') {
                // A step missing its times or days leaves the taper as its order alone, as with the eMAR off.
                if (emarEnabled && row.steps?.length && !addsUnscheduled(row)) {
                    // BnMedicationTaperDialog.save, then onTaperSaved.
                    const carrierDose = doseQuantityOf(carrierOf(med)) ?? null;
                    const built = row.steps.map((step, index) =>
                        buildSegment({
                            medication: med,
                            sequence: index + 1,
                            ...windowSegmentInput(step, { holdReason: step.holdReason }),
                            start,
                            dose: index === 0 ? undefined : taperStepDose(step.doseValue, carrierDose),
                            stepDuration: taperStepDuration(step, { unbounded: isUnboundedTaperStep(index, row.steps.length, item.afterFinal) }),
                            addedBy,
                            addedOn: nowIso,
                            administeringLocation: site,
                        }),
                    );
                    med.dosageInstruction = [...carriers(), ...rechainTaper(built, start)];
                    rechainSegments(med);
                }
                const notes = String(item.taperNotes || '').trim();
                if (notes) med.taperNotes = notes;
            }

            // Last: a scheduled or taper sig reads its days off the segment in force.
            const sig = composeSig(med, { administrationType: type, currentSegment: segmentOn(med, todayIn(timeZone), timeZone) });
            if (sig) {
                const carrier = ensureCarrier(med);
                carrier.timing = mergeCarrierTiming(carrier, { code: { text: sig } });
            }
            return med;
        });
};

/**
 * The transaction that writes the batch: all of it or none of it. Clearing a recorded "no
 * medications" statement rides in the same transaction.
 *
 * @param {Array<MedicationRequest>} medications
 * @param {{nilKnownListId?: string|null}} [options]
 * @returns {{resourceType: 'Bundle', type: 'transaction', entry: Array<object>}}
 */
export const presetApplyBundle = (medications, { nilKnownListId } = {}) => {
    const entry = (medications || []).map((med) => ({ request: { method: 'POST', url: 'MedicationRequest' }, resource: med.toJSON() }));
    if (nilKnownListId) entry.push({ request: { method: 'DELETE', url: `List/${nilKnownListId}` } });
    return { resourceType: 'Bundle', type: 'transaction', entry };
};
